import styled from "styled-components";
import { FinancialReportViewModel } from "../../../../domain/viewmodels/report/FinancialReportViewModel";
import { SortableTableHeader } from "./SortableTableHeader";
import { LoadingSpinner } from "./LoadingSpinner";
import { EmptyState } from "./EmptyState";
import { ErrorState } from "./ErrorState";

interface FinancialReportTableProps {
    data: FinancialReportViewModel[];
    sortBy: string;
    sortDirection: 'asc' | 'desc';
    onSort: (column: string) => void;
    isLoading: boolean;
    error: string | null;
    onRetry: () => void;
}

const columns: { label: string; field: keyof FinancialReportViewModel; className?: string }[] = [
    { label: "Nome do Aluno", field: "studentName", className: "col-name" },
    { label: "Nome do Cliente", field: "customerName", className: "col-name" },
    { label: "CPF do Cliente", field: "customerCpf", className: "col-cpf" },
    { label: "Título do Curso", field: "courseTitle", className: "col-title" },
    { label: "N° NF", field: "invoiceNumber", className: "col-invoice" },
    { label: "Data da Compra", field: "purchaseDate", className: "col-date" },
    { label: "Forma de Pagamento", field: "paymentMethod", className: "col-payment" },
    { label: "Valor Bruto", field: "courseBrutValue", className: "col-value" },
    { label: "Taxa Pagar.me", field: "pagarmeTax", className: "col-value" },
    { label: "% Taxa Pgto", field: "paymentMethodTaxPercent", className: "col-percent" },
    { label: "Imposto Fiscal", field: "fiscalTax", className: "col-invoice" },
    { label: "Custo NF", field: "invoiceCost", className: "col-invoice" },
    { label: "Custo Professor", field: "teacherCost", className: "col-value" },
    { label: "Custo Operacional", field: "operationalCost", className: "col-value" },
    { label: "Receita FMC", field: "fmcRevenue", className: "col-value" },
];

const CURRENCY_FIELDS = new Set<keyof FinancialReportViewModel>([
    "courseBrutValue",
    "pagarmeTax",
    "teacherCost",
    "operationalCost",
    "fmcRevenue",
]);

function formatCurrency(centavos: number): string {
    return (centavos / 100).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
    });
}

export function FinancialReportTable({
    data,
    sortBy,
    sortDirection,
    onSort,
    isLoading,
    error,
    onRetry
}: FinancialReportTableProps) {
    if (isLoading) {
        return <LoadingSpinner />;
    }

    if (error) {
        return <ErrorState message={error} onRetry={onRetry} />;
    }

    if (data.length === 0) {
        return <EmptyState />;
    }

    return (
        <TableWrapper>
            <div className="scroll-container">
                <table className="financial-report-table">
                    <thead>
                        <tr>
                            {columns.map(col => (
                                <SortableTableHeader
                                    key={col.field}
                                    label={col.label}
                                    column={col.field}
                                    className={col.className}
                                    currentSortBy={sortBy}
                                    currentSortDirection={sortDirection}
                                    onSort={onSort}
                                />
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((row, index) => (
                            <tr key={`${row.customerCpf}-${row.purchaseDate}-${index}`}>
                                <td className="col-name">{row.studentName}</td>
                                <td className="col-name">{row.customerName}</td>
                                <td className="col-cpf">{row.customerCpf}</td>
                                <td className="col-title">{row.courseTitle}</td>
                                <td className="col-invoice">{row.invoiceNumber}</td>
                                <td className="col-date">{row.purchaseDate}</td>
                                <td className="col-payment">{row.paymentMethod}</td>
                                <td className="col-value">{formatCurrency(row.courseBrutValue)}</td>
                                <td className="col-value">{formatCurrency(row.pagarmeTax)}</td>
                                <td className="col-percent">{row.paymentMethodTaxPercent.toFixed(2)}%</td>
                                <td className="col-invoice">{row.fiscalTax}</td>
                                <td className="col-invoice">{row.invoiceCost}</td>
                                <td className="col-value">{formatCurrency(row.teacherCost)}</td>
                                <td className="col-value">{formatCurrency(row.operationalCost)}</td>
                                <td className="col-value">{formatCurrency(row.fmcRevenue)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="footer-warning">
                Campos de Nota Fiscal indisponíveis até a definição e integração com o novo emissor
            </div>
        </TableWrapper>
    );
}

const TableWrapper = styled.div`
    margin-top: 0;

    .scroll-container {
        overflow-x: auto;
        overflow-y: auto;
        min-height: 70vh;
        max-height: 70vh;
    }

    .financial-report-table {
        font-family: Lato;
        font-size: 0.85rem;
        min-width: 1750px;
        width: 100%;
        border-collapse: separate;
        border-spacing: 0 6px;
        table-layout: fixed;

        .col-name    { width: 140px; }
        .col-cpf     { width: 120px; }
        .col-title   { width: 160px; }
        .col-invoice { width: 90px; }
        .col-date    { width: 110px; }
        .col-payment { width: 110px; }
        .col-value   { width: 110px; }
        .col-percent { width: 90px; }

        thead {
            position: sticky;
            top: 0;
            z-index: 1;

            tr {
                background-color: #FFFFFF;
            }

            th {
                background-color: #FFFFFF;
                border: none;
                font-size: 0.82rem;
                font-weight: 600;
                color: #514F4E;
                padding: 0.4rem 0.75rem;
                vertical-align: middle;
            }
        }

        tbody {
            tr {
                background-color: #FFFFFF;
                box-shadow: 0px 1px 2px rgba(75, 75, 75, 0.12);
                border-radius: 4px;
            }

            td {
                border: none;
                padding: 0.65rem 0.75rem;
                vertical-align: middle;
                color: #514F4E;
                font-size: 0.85rem;
                background-color: #FFFFFF;
            }

            td:first-child {
                border-radius: 4px 0 0 4px;
            }

            td:last-child {
                border-radius: 0 4px 4px 0;
            }

            .col-value {
                font-variant-numeric: tabular-nums;
            }
        }
    }

    .footer-warning {
        background-color: #FFF3E0;
        border-top: 3px solid #F5A623;
        color: #D4800A;
        font-size: 0.82rem;
        padding: 0.6rem 1rem;
        margin-top: 0.25rem;
    }
`;
